import {
  ArrowBack,
  ArrowForward,
  BadgeOutlined,
  Check,
  Payment,
  Search,
  ShoppingBagOutlined,
} from "@mui/icons-material";
import { Alert, Box, Button, Chip, InputAdornment, TextField, Typography } from "@mui/material";
import PropTypes from "prop-types";
import React from "react";
import BenefitSelector from "./BenefitSelector";
import CustomerLookup from "./CustomerLookup";
import PaymentSelector from "./PaymentSelector";
import ProductGrid from "./ProductGrid";
import { formatCurrency } from "../utils";

// Mismo tope que el backend (OBSERVACIONES_MAX en PuntoVentaEventoService):
// lo que protege es el papel de 80 mm de la comanda.
const OBSERVACIONES_MAX = 300;

const steps = [
  { label: "Productos", icon: ShoppingBagOutlined },
  { label: "Cliente", icon: BadgeOutlined },
  { label: "Cobro", icon: Payment },
];

const secondaryButtonSx = {
  borderRadius: "12px",
  borderColor: "#CBD5E1",
  bgcolor: "#fff",
  color: "#475569",
  textTransform: "none",
  fontWeight: 800,
  "&:hover": { borderColor: "#2C3087", bgcolor: "#F8F7FF", color: "#2C3087", opacity: 1 },
};

const primaryButtonSx = {
  borderRadius: "12px",
  bgcolor: "#2C3087",
  color: "#fff",
  textTransform: "none",
  fontWeight: 900,
  boxShadow: "0 8px 18px rgba(44, 48, 135, .2)",
  "&:hover": { bgcolor: "#22266F" },
  "&.Mui-disabled": { bgcolor: "#E2E8F0 !important", color: "#64748B !important", boxShadow: "none" },
};

export default function SaleProcessPanel({
  activeStep,
  products,
  cart,
  cartItems,
  customer,
  customerCandidates = [],
  customerLoading = false,
  customerError = "",
  sampleCustomers,
  avisos = [],
  paymentMethods,
  paymentCode,
  vouchers,
  voucherCode,
  observaciones,
  totals,
  onStepChange,
  onAddProduct,
  onQuantityChange,
  onCustomerLookup,
  onCustomerSelect,
  onCustomerReset,
  onAnonymousCustomer,
  onPaymentChange,
  onVoucherChange,
  onObservacionesChange,
  onCheckout,
}) {
  const [productCategory, setProductCategory] = React.useState("Todos");
  const [productSearch, setProductSearch] = React.useState("");
  const totalUnits = cartItems.reduce((sum, item) => sum + item.quantity, 0);
  const voucherReady = !voucherCode || totals.voucherCanApply;
  const paymentRequired = totals.amountDue > 0.01;
  const productCategories = React.useMemo(
    () => ["Todos", ...new Set(products.map((product) => product.category))],
    [products]
  );

  const stepIsEnabled = (stepIndex) => {
    if (stepIndex === 0) return true;
    if (stepIndex === 1) return totalUnits > 0 && voucherReady;
    return totalUnits > 0 && voucherReady && Boolean(customer) && !customerLoading && paymentRequired;
  };

  const stepIsComplete = (stepIndex) => {
    if (stepIndex === 0) return totalUnits > 0 && voucherReady;
    if (stepIndex === 1) return Boolean(customer) && !customerLoading;
    return Boolean(customer) && !customerLoading && !paymentRequired;
  };

  return (
    <Box
      data-testid="sale-process-panel"
      sx={{ bgcolor: "#fff", border: "1px solid #E2E8F0", borderRadius: "20px", overflow: "hidden", minWidth: 0 }}
    >
      <Box
        component="nav"
        aria-label="Pasos de la venta"
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
          gap: 0.75,
          p: 0.75,
          bgcolor: "#F8FAFC",
          borderBottom: "1px solid #E2E8F0",
        }}
      >
        {steps.map((step, index) => {
          const StepIcon = step.icon;
          const enabled = stepIsEnabled(index);
          const complete = stepIsComplete(index);
          const selected = activeStep === index;

          return (
            <Box
              key={step.label}
              component="button"
              type="button"
              data-testid={`sale-step-${index}`}
              aria-current={selected ? "step" : undefined}
              disabled={!enabled}
              onClick={() => onStepChange(index)}
              sx={{
                minHeight: 48,
                px: { xs: 0.75, sm: 1.25 },
                border: selected ? "1px solid #DAD9F4" : "1px solid transparent",
                borderRadius: "13px",
                bgcolor: selected ? "#fff" : "transparent",
                color: selected ? "#2C3087" : enabled ? "#475569" : "#AAB4C3",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.8,
                cursor: enabled ? "pointer" : "not-allowed",
                fontFamily: "inherit",
                boxShadow: selected ? "0 4px 12px rgba(15, 23, 42, .06)" : "none",
                transition: "all 130ms ease",
                "&:hover": enabled ? { bgcolor: selected ? "#fff" : "#F1F5F9" } : undefined,
                "&:focus-visible": { outline: "3px solid rgba(44, 48, 135, .22)" },
              }}
            >
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  display: "grid",
                  placeItems: "center",
                  bgcolor: complete ? "#E8F7F2" : selected ? "#F3F2FF" : "#EEF2F7",
                  color: complete ? "#18735F" : "inherit",
                  flexShrink: 0,
                }}
              >
                {complete ? <Check sx={{ fontSize: 17 }} /> : <StepIcon sx={{ fontSize: 17 }} />}
              </Box>
              <Box sx={{ textAlign: "left", display: { xs: "none", sm: "block" } }}>
                <Typography component="span" sx={{ display: "block", fontSize: 10, fontWeight: 900, lineHeight: 1, opacity: 0.7 }}>
                  PASO {index + 1}
                </Typography>
                <Typography component="span" sx={{ display: "block", fontSize: 13, fontWeight: 900, lineHeight: 1.25, mt: 0.2 }}>
                  {step.label}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>

      {avisos.map((aviso) => (
        <Alert key={aviso.codigo} severity="warning" sx={{ mx: 1.5, mt: 1.5 }}>
          {aviso.mensaje}
        </Alert>
      ))}

      {activeStep === 0 && (
        <Box sx={{ p: { xs: 1.5, md: 1.75 } }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "auto minmax(0, 1fr) 230px" },
              alignItems: { xs: "stretch", md: "center" },
              gap: { xs: 1, md: 1.5 },
              mb: 1.35,
            }}
          >
            <Box sx={{ minWidth: 185 }}>
              <Typography sx={{ fontSize: 10.5, fontWeight: 900, color: "#2C3087", letterSpacing: ".08em", lineHeight: 1.1 }}>
                CATÁLOGO DEL EVENTO
              </Typography>
              <Typography component="h2" sx={{ fontSize: 18, fontWeight: 900, color: "#172033", lineHeight: 1.2, mt: 0.25 }}>
                Elegí productos
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 0.55, flexWrap: "wrap", alignItems: "center" }}>
              {productCategories.map((category) => (
                <Chip
                  key={category}
                  label={category}
                  data-testid={`category-${category.toLowerCase()}`}
                  onClick={() => setProductCategory(category)}
                  sx={{
                    height: 30,
                    px: 0.25,
                    fontSize: 11.5,
                    fontWeight: 800,
                    color: productCategory === category ? "#fff" : "#475569",
                    bgcolor: productCategory === category ? "#2C3087" : "#F1F5F9",
                    "&:hover": { bgcolor: productCategory === category ? "#23266B" : "#E2E8F0" },
                  }}
                />
              ))}
            </Box>
            <TextField
              size="small"
              placeholder="Buscar producto"
              value={productSearch}
              onChange={(event) => setProductSearch(event.target.value)}
              inputProps={{ "aria-label": "Buscar producto" }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ width: "100%", "& .MuiOutlinedInput-root": { height: 38, bgcolor: "#fff" } }}
            />
          </Box>
          <BenefitSelector
            vouchers={vouchers}
            selectedCode={voucherCode}
            onChange={onVoucherChange}
          />
          {totals.voucherError && <Alert severity="warning" sx={{ mb: 1.5 }}>{totals.voucherError}</Alert>}
          <ProductGrid
            products={products}
            cart={cart}
            category={productCategory}
            search={productSearch}
            voucher={vouchers.find((item) => item.code === voucherCode) || null}
            voucherCoveredUnits={totals.voucherCoveredUnits}
            voucherCoverageByProduct={totals.voucherCoverageByProduct}
            onAdd={onAddProduct}
            onQuantityChange={onQuantityChange}
          />
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1.5 }}>
            <Button
              variant="contained"
              endIcon={<ArrowForward />}
              disabled={totalUnits === 0 || !voucherReady}
              onClick={() => onStepChange(1)}
              data-testid="continue-to-customer"
              sx={{ ...primaryButtonSx, minWidth: 220 }}
            >
              {!totalUnits
                ? "Seleccioná productos"
                : !voucherReady
                  ? "Completá el papel o quitá el voucher"
                  : `Continuar con ${totalUnits} ${totalUnits === 1 ? "producto" : "productos"}`}
            </Button>
          </Box>
        </Box>
      )}

      {activeStep === 1 && (
        <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 760, width: "100%", mx: "auto" }}>
          <Typography sx={{ fontSize: 10.5, fontWeight: 900, color: "#2C3087", letterSpacing: ".08em" }}>
            PASO 2 · CLIENTE
          </Typography>
          <Typography component="h2" sx={{ fontSize: 23, fontWeight: 900, color: "#172033", lineHeight: 1.2, mt: 0.35 }}>
            Identificá al cliente
          </Typography>
          <Typography sx={{ color: "#64748B", fontSize: 13.5, mt: 0.5, mb: 2.5 }}>
            Consultá el DNI para aplicar la condición fiscal correspondiente o continuá como consumidor final.
          </Typography>
          <Box sx={{ p: { xs: 1.5, sm: 2 }, border: "1px solid #E2E8F0", borderRadius: "16px", bgcolor: "#FBFCFE" }}>
            <CustomerLookup
              key={customer ? customer.id || customer.dni || "consumer" : "unidentified"}
              customer={customer}
              candidates={customerCandidates}
              loading={customerLoading}
              externalError={customerError}
              sampleCustomers={sampleCustomers}
              showHeader={false}
              onLookup={onCustomerLookup}
              onSelect={onCustomerSelect}
              onReset={onCustomerReset}
              onAnonymous={onAnonymousCustomer}
            />
          </Box>
          <Box sx={{ mt: 1.5, p: { xs: 1.5, sm: 2 }, border: "1px solid #E2E8F0", borderRadius: "16px", bgcolor: "#FBFCFE" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", mb: 0.75 }}>
              <Typography component="label" htmlFor="venta-evento-observaciones" sx={{ fontSize: 12, fontWeight: 900, color: "#2C3087", letterSpacing: ".08em" }}>
                OBSERVACIONES
              </Typography>
              <Typography sx={{ fontSize: 11, color: "#94A3B8" }}>
                {observaciones.length}/{OBSERVACIONES_MAX}
              </Typography>
            </Box>
            <TextField
              id="venta-evento-observaciones"
              fullWidth
              multiline
              minRows={2}
              maxRows={4}
              size="small"
              placeholder="Aclaraciones para la comanda (ej.: sin cebolla, retira más tarde)"
              value={observaciones}
              onChange={(event) => onObservacionesChange(event.target.value.slice(0, OBSERVACIONES_MAX))}
              inputProps={{ maxLength: OBSERVACIONES_MAX }}
              data-testid="sale-observaciones"
              // El tema deja el input en max-content y centra el contenido:
              // para un textarea hay que devolverle el ancho y el bloque.
              sx={{
                "& .MuiInputBase-root": { display: "block !important", py: 1, px: 1.5 },
                "& textarea": { width: "100% !important", display: "block", resize: "none" },
                "& textarea::placeholder": { color: "#94A3B8 !important", opacity: 1 },
              }}
            />
            <Typography sx={{ fontSize: 11, color: "#64748B", mt: 0.75 }}>
              Se imprimen en la comanda para la cocina.
            </Typography>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1.25, mt: 2.5 }}>
            <Button variant="outlined" startIcon={<ArrowBack />} onClick={() => onStepChange(0)} sx={secondaryButtonSx}>
              Productos
            </Button>
            <Button
              variant="contained"
              endIcon={<ArrowForward />}
              disabled={!customer || customerLoading}
              onClick={() => paymentRequired ? onStepChange(2) : onCheckout()}
              data-testid="continue-to-payment"
              sx={{ ...primaryButtonSx, minWidth: 190 }}
            >
              {paymentRequired ? "Continuar al cobro" : "Confirmar venta bonificada"}
            </Button>
          </Box>
        </Box>
      )}

      {activeStep === 2 && (
        <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 820, width: "100%", mx: "auto" }}>
          <Typography sx={{ fontSize: 10.5, fontWeight: 900, color: "#2C3087", letterSpacing: ".08em" }}>
            PASO 3 · COBRO
          </Typography>
          <Typography component="h2" sx={{ fontSize: 23, fontWeight: 900, color: "#172033", lineHeight: 1.2, mt: 0.35 }}>
            Elegí el medio de pago
          </Typography>
          <Typography sx={{ color: "#64748B", fontSize: 13.5, mt: 0.5, mb: 2.5 }}>
            El importe a cobrar ya tiene aplicado el voucher, si lo hubo.
          </Typography>
          <Box sx={{ p: { xs: 1.5, sm: 2 }, border: "1px solid #E2E8F0", borderRadius: "16px", bgcolor: "#FBFCFE" }}>
            <PaymentSelector
              methods={paymentMethods}
              selectedCode={paymentCode}
              showHeader={false}
              onChange={onPaymentChange}
            />
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: { xs: "stretch", sm: "center" },
              justifyContent: "space-between",
              flexDirection: { xs: "column", sm: "row" },
              gap: 1.25,
              mt: 2.5,
            }}
          >
            <Button variant="outlined" startIcon={<ArrowBack />} onClick={() => onStepChange(1)} sx={secondaryButtonSx}>
              Cliente
            </Button>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1.5 }}>
              <Box sx={{ textAlign: "right" }}>
                <Typography sx={{ color: "#64748B", fontSize: 11, fontWeight: 700 }}>
                  A COBRAR
                </Typography>
                <Typography sx={{ color: "#172033", fontSize: 23, lineHeight: 1, fontWeight: 900 }}>
                  {formatCurrency(totals.amountDue)}
                </Typography>
              </Box>
              <Button
                variant="contained"
                onClick={onCheckout}
                data-testid="checkout-button"
                // Sin un medio elegido no hay cobro posible: el backend resuelve
                // el MedioPago a partir de este código.
                disabled={customerLoading || (totals.amountDue > 0 && !paymentCode)}
                sx={{ ...primaryButtonSx, minHeight: 48, minWidth: 180, fontSize: 15 }}
              >
                {totals.amountDue > 0 ? "Iniciar cobro" : "Registrar voucher"}
              </Button>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}

SaleProcessPanel.propTypes = {
  activeStep: PropTypes.number.isRequired,
  products: PropTypes.arrayOf(PropTypes.object).isRequired,
  cart: PropTypes.object.isRequired,
  cartItems: PropTypes.arrayOf(PropTypes.object).isRequired,
  customer: PropTypes.object,
  customerCandidates: PropTypes.arrayOf(PropTypes.object),
  customerLoading: PropTypes.bool,
  customerError: PropTypes.string,
  sampleCustomers: PropTypes.arrayOf(PropTypes.object).isRequired,
  avisos: PropTypes.arrayOf(PropTypes.object),
  paymentMethods: PropTypes.arrayOf(PropTypes.object).isRequired,
  paymentCode: PropTypes.string.isRequired,
  vouchers: PropTypes.arrayOf(PropTypes.object).isRequired,
  voucherCode: PropTypes.string.isRequired,
  observaciones: PropTypes.string.isRequired,
  totals: PropTypes.object.isRequired,
  onStepChange: PropTypes.func.isRequired,
  onAddProduct: PropTypes.func.isRequired,
  onQuantityChange: PropTypes.func.isRequired,
  onCustomerLookup: PropTypes.func.isRequired,
  onCustomerSelect: PropTypes.func.isRequired,
  onCustomerReset: PropTypes.func.isRequired,
  onAnonymousCustomer: PropTypes.func.isRequired,
  onPaymentChange: PropTypes.func.isRequired,
  onVoucherChange: PropTypes.func.isRequired,
  onObservacionesChange: PropTypes.func.isRequired,
  onCheckout: PropTypes.func.isRequired,
};
